/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container } from '../../../../components/container';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { formatDate } from '../../../../utilities';
import { DatePickerValue } from '../../../../components/date-picker';
import { DateTime } from '../../interfaces';
import { ColorPalette } from '../../../../components/config';
import { excludeClosedDays, isClosedDay } from '../../utils';

interface IWarningLessonConfirmDialogProps {
  isOpen: boolean;
  overlappingDates: DateTime[];
  reoccuringDates: Date[];
  isReoccuringLesson: boolean;
  isMovingLesson: boolean;
  skipClosedDaysEnabled: boolean;
  centerClosedDaysEnabled: boolean;
  centerClosedDaysMissing: boolean;
  startDate: DatePickerValue;
  endDate: DatePickerValue;
  isSaving: boolean;
  onConfirmClick: (skipClosedDays: boolean) => void;
  onCancelClick: () => void;
}

export function LessonWarningConfirmDialog(props: IWarningLessonConfirmDialogProps) {
  const { isOpen, onConfirmClick, onCancelClick, overlappingDates, reoccuringDates, startDate, endDate, isReoccuringLesson, isMovingLesson, skipClosedDaysEnabled, centerClosedDaysEnabled, centerClosedDaysMissing, isSaving } = props;
  const [includeClosedDays, setIncludeClosedDays] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIncludeClosedDays(false);
    }
  }, [isOpen]);

  const closedDays = overlappingDates.filter(isClosedDay);
  const hasClosedDays = closedDays.length > 0;

  const lessons = overlappingDates.filter(x => x.dateType === 'lesson');
  const hasLessonOverlap = lessons.length > 0;

  const hasOverlappingDates = overlappingDates.length > 0;

  const lessonsToCreate = skipClosedDaysEnabled && !includeClosedDays ? excludeClosedDays(reoccuringDates, closedDays).length : reoccuringDates.length;

  function getTitle() {
    if (isMovingLesson) {
      return 'Flyt lektion';
    }
    return isReoccuringLesson ? `Opret ${lessonsToCreate} lektioner` : 'Opret lektion';
  }

  function getClosedDaysText() {
    if (isMovingLesson) {
      return 'Lektionen flyttes til en lukkedag. Vil du fortsætte?';
    }
    return isReoccuringLesson ? 'Følgende datoer er lukkedage:' : 'Lektionen oprettes på en lukkedag. Vil du fortsætte?';
  }

  return (
    <Dialog open={isOpen} fullWidth>
      <DialogTitle>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <FontAwesomeIcon
            size="xl"
            icon={hasOverlappingDates ? faExclamationTriangle : faInfoCircle}
            color={hasOverlappingDates ? ColorPalette.HotAurora : ColorPalette.InfoIcon}
          />
          {getTitle()}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Container>
          {hasClosedDays && !skipClosedDaysEnabled && (
            <Row>
              <Cell size="full">
                <OverlapWarnings
                  overlappingDates={closedDays}
                  header={centerClosedDaysEnabled ? 'Lukkedage' : 'Helligdage'}
                  text={`Der vil blive oprettet lektioner på flg. ${centerClosedDaysEnabled ? 'lukkedage' : 'helligdage'}. Vil du fortsætte?`}
                  showNames={centerClosedDaysEnabled}
                  showClosedDayKind={centerClosedDaysEnabled}
                />
              </Cell>
            </Row>
          )}
          {hasClosedDays && skipClosedDaysEnabled && (
            <Row>
              <Cell size="full">
                <OverlapWarnings overlappingDates={closedDays} header="Lukkedage" text={getClosedDaysText()} showNames showClosedDayKind={centerClosedDaysEnabled} />
                {isReoccuringLesson && (
                  <FormControlLabel
                    control={<Checkbox checked={includeClosedDays} onChange={event => setIncludeClosedDays(event.target.checked)} />}
                    label={`Der planlægges generelt ikke lektioner på lukkedage. ${closedDays.length === 1 ? 'Opret 1 lektion på lukkedagen' : `Opret ${closedDays.length} lektioner på lukkedage`}`}
                  />
                )}
              </Cell>
            </Row>
          )}
          {hasLessonOverlap && (
            <Row>
              <Cell size="full">
                <OverlapWarnings overlappingDates={lessons} header="Lektioner" text="Der findes allerede lektioner på flg. dage. Vil du fortsætte?" />
              </Cell>
            </Row>
          )}
          {isReoccuringLesson && (
            <Row>
              <Cell size="full">{`Oprettes mellem ${formatDate(startDate)} og ${formatDate(endDate)}`}.</Cell>
            </Row>
          )}
          {centerClosedDaysMissing && (
            <Row>
              <Cell size="full">
                <Alert severity="info">Holdet har intet ansvarligt team med et center, så centrets lukkedage er ikke med.</Alert>
              </Cell>
            </Row>
          )}
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelClick} variant="outlined" disabled={isSaving}>
          Afbryd
        </Button>
        <LoadingButton onClick={() => onConfirmClick(!includeClosedDays)} variant="contained" color="primary" loading={isSaving}>
          Gem
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

interface IOverlapWarningsProps {
  overlappingDates: DateTime[];
  header: string;
  text: string;
  showNames?: boolean;
  showClosedDayKind?: boolean;
}

function OverlapWarnings(props: IOverlapWarningsProps) {
  const { overlappingDates, header, text, showNames, showClosedDayKind } = props;

  function getLabel(date: DateTime) {
    const label = showNames && date.name ? `${formatDate(date.start)} ${date.name}` : formatDate(date.start);
    if (!showClosedDayKind) {
      return label;
    }
    return `${label} (${date.dateType === 'centerClosedDay' ? 'lukkedag' : 'helligdag'})`;
  }

  return (
    <Box>
      <Box sx={{ fontWeight: 600 }}>{header}</Box>
      <Box>{text}</Box>
      <ul>
        {overlappingDates.map((x, i) => (
          <li key={i}>{getLabel(x)}</li>
        ))}
      </ul>
    </Box>
  );
}
